import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Unlock, Lock, XCircle } from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import { setSubPromoterStatus } from "../../services/eventDetailService";
import { color, radius } from "../../styles/theme";

/**
 * Sub-promoter status change, ported from mobile's
 * remove-bs-for-subpromoter/index.js (misleadingly named there — it's a
 * status-change sheet, not removal). Real backend enum (WebAPI/Enums/Enums.cs
 * SubPromoterStatus): 1=Active, 2=NonPublic, 3=Deactivated, 4=Pending. This
 * previously mislabeled value 2 as "Set Pending" (a status this UI never
 * actually offers — Pending is 4) instead of what it really is: a
 * public/private activation choice. Set Pending is removed; reactivating a
 * Deactivated (or switching an Active) Sub-Promoter now offers Activate
 * Publicly (1, full access incl. chat) vs Activate Privately (2, gallery
 * access only — chat disabled, per SubPromoterService.cs's status-change
 * comments) — matching isSubPromoterDeactivate = SubPromoterStatus === 3
 * elsewhere in the mobile app.
 */
const STATUS_OPTION_DEFS = [
  { value: 1, key: "activatePublic", icon: Unlock, tone: "success" },
  { value: 2, key: "activatePrivate", icon: Lock, tone: "info" },
  { value: 3, key: "deactivate", icon: XCircle, tone: "danger", danger: true },
];

const TONE = {
  success: { bg: color.success50, fg: color.success500 },
  info: { bg: color.info50, fg: color.info500 },
  danger: { bg: color.danger50, fg: color.danger500 },
};

const SubPromoterStatusModal = ({ target, onClose, onUpdated }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [reason, setReason] = useState("");
  if (!target) return null;

  const STATUS_OPTIONS = STATUS_OPTION_DEFS.map((o) => ({
    ...o,
    label: t(`subPromoterStatusModal.options.${o.key}.label`),
    description: t(`subPromoterStatusModal.options.${o.key}.description`),
  }));
  const options = STATUS_OPTIONS.filter((o) => o.value !== target.currentStatus);

  const handleSetStatus = async (status) => {
    if (status === 3 && selectedStatus !== 3) {
      setSelectedStatus(3);
      return;
    }
    if (status === 3 && !reason.trim()) {
      toast.warning(t("subPromoterStatusModal.reasonRequired"));
      return;
    }
    setLoadingStatus(status);
    try {
      await setSubPromoterStatus(target.chatRoomId, target.id, status, status === 3 ? reason.trim() : null);
      toast.success(t("subPromoterStatusModal.updateSuccess", { name: target.name }));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("subPromoterStatusModal.updateError"));
    } finally {
      setLoadingStatus(null);
    }
  };

  return (
    <Modal title={t("subPromoterStatusModal.title")} subtitle={target.name} onClose={onClose} busy={loadingStatus != null} maxWidth={400}
      footer={<Button variant="secondary" onClick={onClose} disabled={loadingStatus != null}>{t("common:actions.cancel")}</Button>}
    >
      <div style={s.list}>
        {options.map((opt) => {
          const tone = TONE[opt.tone];
          return (
            <button
              key={opt.value}
              type="button"
              className="row-link"
              style={s.item}
              onClick={() => handleSetStatus(opt.value)}
              disabled={loadingStatus != null}
            >
              <span style={{ ...s.itemIcon, background: tone.bg, color: tone.fg }}>
                <opt.icon size={16} strokeWidth={2} />
              </span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <span style={s.itemLabel}>{loadingStatus === opt.value ? t("subPromoterStatusModal.updating") : opt.label}</span>
                <span style={s.itemDesc}>{opt.description}</span>
              </span>
            </button>
          );
        })}
        {selectedStatus === 3 && (
          <div style={s.reasonBox}>
            <label htmlFor="sub-promoter-deactivation-reason" style={s.reasonLabel}>
              {t("subPromoterStatusModal.reasonLabel")}
            </label>
            <textarea
              id="sub-promoter-deactivation-reason"
              value={reason}
              maxLength={1000}
              rows={4}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("subPromoterStatusModal.reasonPlaceholder")}
              style={s.reasonInput}
              autoFocus
            />
            <div style={s.reasonFooter}>
              <span>{reason.length}/1000</span>
              <Button
                variant="danger"
                onClick={() => handleSetStatus(3)}
                disabled={loadingStatus != null || !reason.trim()}
              >
                {loadingStatus === 3 ? t("subPromoterStatusModal.updating") : t("subPromoterStatusModal.confirmDeactivate")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 8 },
  item: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 14px", borderRadius: radius.sm, border: `1px solid ${color.border}`, background: color.surface,
  },
  itemIcon: {
    width: 32, height: 32, borderRadius: radius.sm, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  itemLabel: { display: "block", fontSize: 13.5, fontWeight: 700, color: color.textPrimary },
  itemDesc: { display: "block", fontSize: 11.5, color: color.textMuted, marginTop: 1 },
  reasonBox: { padding: 12, borderRadius: radius.sm, background: color.danger50 },
  reasonLabel: { display: "block", fontSize: 12, fontWeight: 700, color: color.textPrimary, marginBottom: 6 },
  reasonInput: { width: "100%", boxSizing: "border-box", resize: "vertical", border: `1px solid ${color.border}`, borderRadius: radius.sm, padding: 10, font: "inherit", color: color.textPrimary, background: color.surface },
  reasonFooter: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 8, fontSize: 11, color: color.textMuted },
};

export default SubPromoterStatusModal;
